import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Flame, Loader2 } from 'lucide-react';
export interface UpsellProduct {
  id: string;
  name: string;
  variantId: string;
  normalPrice: number;
  offerPrice: number;
  benefit: string;
  image: string;
}
interface UpsellModalProps {
  isOpen: boolean;
  mainProductPrice: number;
  upsellProducts: UpsellProduct[];
  onConfirm: (selectedIds: string[]) => void;
  onClose: () => void;
}
const formatARS = (value: number) => `$${value.toLocaleString('es-AR', {
  minimumFractionDigits: 0
})}`;
export function UpsellModal({
  isOpen,
  mainProductPrice,
  upsellProducts,
  onConfirm,
  onClose
}: UpsellModalProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSelected(new Set());
      setIsProcessing(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const toggle = (id: string) => {
    if (isProcessing) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const {
    upsellsTotal,
    grandTotal,
    selectedCount
  } = useMemo(() => {
    const items = upsellProducts.filter(p => selected.has(p.id));
    const upsellsTotal = items.reduce((sum, p) => sum + p.offerPrice, 0);
    return {
      upsellsTotal,
      grandTotal: mainProductPrice + upsellsTotal,
      selectedCount: items.length
    };
  }, [selected, upsellProducts, mainProductPrice]);
  const handlePrimary = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    onConfirm(Array.from(selected));
  };
  const handleSecondary = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    onConfirm([]);
  };
  return <AnimatePresence data-id="element-209">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center md:p-6" data-id="element-210">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-211" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 280
      }} className="relative w-full max-w-lg bg-white rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] md:max-h-[90vh]" data-id="element-212">
            {/* Header */}
            <div className="relative px-5 pt-5 pb-4 md:px-7 md:pt-6 border-b border-gray-100 bg-gradient-to-b from-[#FAF7F2] to-white" data-id="element-213">
              <button onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors shadow-sm border border-gray-100" data-id="element-214">
                <X className="w-4 h-4" data-id="element-215" />
              </button>

              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-terracotta/10 text-terracotta text-[10px] font-bold uppercase tracking-widest mb-3" data-id="element-216">
                <Flame className="w-3 h-3 fill-current" data-id="element-217" />
                Oferta Relámpago
              </div>

              <h2 className="font-serif text-lg md:text-xl font-bold text-brown leading-tight mb-1.5 pr-8" data-id="element-218">
                Agregá tus complementos favoritos
              </h2>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed" data-id="element-219">
                Seleccioná los ebooks que querés sumar a tu Pack Holístico antes
                de finalizar tu compra.
              </p>
            </div>

            {/* Scrollable products list */}
            <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5 space-y-3 bg-gray-50/50" data-id="element-220">
              {upsellProducts.map(product => {
            const isSelected = selected.has(product.id);
            return <button key={product.id} type="button" onClick={() => toggle(product.id)} disabled={isProcessing} className={`w-full text-left rounded-xl border-2 transition-all duration-200 p-3 md:p-4 flex items-stretch gap-3 bg-white ${isSelected ? 'border-terracotta shadow-md ring-2 ring-terracotta/10' : 'border-gray-200 hover:border-gray-300'} disabled:opacity-70 disabled:cursor-not-allowed`} data-id="element-221">
                    {/* Image */}
                    <div className="flex-shrink-0 w-16 md:w-20 rounded-md overflow-hidden bg-gray-100 ring-1 ring-black/5 shadow-sm self-start" data-id="element-222">
                      <img src={product.image} alt={product.name} className="block w-full h-auto" data-id="element-223" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 flex flex-col" data-id="element-224">
                      <div className="inline-flex w-fit items-center px-1.5 py-0.5 rounded-sm bg-gold/15 text-gold-dark text-[8px] font-bold uppercase tracking-widest mb-1" style={{
                  color: '#9A7B2E'
                }} data-id="element-225">
                        Oferta especial
                      </div>
                      <h3 className="font-serif text-[13px] md:text-sm font-bold text-brown leading-snug mb-1 uppercase tracking-tight" data-id="element-226">
                        {product.name}
                      </h3>
                      <p className="text-[11px] md:text-xs text-gray-600 leading-relaxed mb-2 flex-1" data-id="element-227">
                        {product.benefit}
                      </p>
                      <div className="flex items-baseline gap-2 tabular-nums" data-id="element-228">
                        <span className="text-[11px] text-gray-400 line-through" data-id="element-229">
                          {formatARS(product.normalPrice)}
                        </span>
                        <span className="text-base font-bold text-terracotta tracking-tight" data-id="element-230">
                          {formatARS(product.offerPrice)}
                        </span>
                      </div>
                    </div>

                    {/* Checkbox */}
                    <div className="flex-shrink-0 self-center" data-id="element-231">
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${isSelected ? 'bg-terracotta border-2 border-terracotta' : 'bg-white border-2 border-gray-300'}`} data-id="element-232">
                        {isSelected && <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-233" />}
                      </div>
                    </div>
                  </button>;
          })}

              <p className="text-[10px] md:text-xs text-gray-500 text-center px-2 pt-2 leading-relaxed" data-id="element-234">
                Se agrega automáticamente a tu compra y recibís todo junto en
                formato digital.
              </p>
            </div>

            {/* Sticky footer */}
            <div className="border-t border-gray-100 bg-white px-4 py-3 md:px-6 md:py-4 shadow-[0_-8px_24px_rgba(0,0,0,0.04)]" data-id="element-235">
              {selectedCount > 0 && <div className="mb-3 tabular-nums" data-id="element-236">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1" data-id="element-237">
                    <span data-id="element-238">Complementos ({selectedCount}):</span>
                    <span className="font-medium" data-id="element-239">
                      {formatARS(upsellsTotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm font-semibold text-brown border-t border-gray-100 pt-1.5" data-id="element-240">
                    <span data-id="element-241">Total de tu compra:</span>
                    <span className="text-base font-bold text-terracotta tracking-tight" data-id="element-242">
                      {formatARS(grandTotal)}
                    </span>
                  </div>
                </div>}

              <button onClick={handlePrimary} disabled={isProcessing} className="w-full bg-terracotta hover:bg-terracotta-dark text-white font-bold uppercase tracking-wide py-3.5 px-4 rounded-lg shadow-premium active:scale-[0.98] transition-all text-sm md:text-base mb-1.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-243">
                {isProcessing ? <>
                    <Loader2 className="w-4 h-4 animate-spin" data-id="element-244" />
                    Redirigiendo...
                  </> : selectedCount === 0 ? 'CONTINUAR SOLO CON MI COMPRA' : <>
                    AGREGAR A MI PEDIDO POR{' '}
                    <span className="tabular-nums" data-id="element-245">
                      {formatARS(upsellsTotal)}
                    </span>
                  </>}
              </button>

              {selectedCount > 0 && <button onClick={handleSecondary} disabled={isProcessing} className="w-full text-gray-500 hover:text-gray-800 font-medium py-2 text-xs md:text-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-246">
                  Seguir sin agregar
                </button>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}